import { importe as formatearEnDivisa, fecha as formatearFecha } from '../formato.js';

/**
 * BLOQUE 2 · CORTE EN CURSO — informativo y SUBORDINADO (RF-123, RF-126, D-189).
 *
 * QUE NO SE CONFUNDA CON EL PERIODO EXIGIBLE ES REQUISITO, NO PREFERENCIA ESTÉTICA
 * (RN-284). Por eso, y todo esto es deliberado:
 *   · va DEBAJO y con sangría, colgando de una línea gris: depende del renglón, no
 *     compite con él;
 *   · encabezado literal «Corte en curso — todavía no se paga»;
 *   · NINGÚN color de estado: solo neutros. Ni rojo, ni ámbar, ni verde;
 *   · el importe va con jerarquía tipográfica MENOR que la del exigible;
 *   · sin insignia de estado, sin días para vencer y sin la palabra «por pagar».
 * Si en una prueba de pasillo alguien confunde este importe con lo que debe pagar,
 * la pantalla está mal aunque las cifras sean correctas.
 *
 * NO TIENE ESTADO: no es «por vencer», no es «vencida», no es «pagada» y no es
 * «sin saldo». No genera aviso, no suma a ningún total y no altera el orden
 * (RN-283). Por eso el objeto que recibe NO trae ninguno de esos campos.
 */
export function BloqueDeCorteEnCurso({ corte, codigoISO, conDesglose = false }) {
  if (!corte) return null;

  return (
    <section className="curso" aria-label="Corte en curso, informativo" data-prueba="corte-en-curso">
      <div className="curso-cab">
        <span aria-hidden="true">◷</span> Corte en curso — todavía no se paga
      </div>

      <div className="curso-cuerpo">
        {corte.advertencia ? (
          <div className="aviso s" role="status" data-prueba="corte-en-curso-advertencia">
            ⚠ {corte.advertencia}
            {/* [BARRIDO DE LENGUAJE · incremento 18/21 · D-401, RF-222, RN-626,
                RN-627] Este componente lo dibujan DOS pantallas (el panel y el
                detalle de la tarjeta) y por eso se quedó fuera del barrido de
                siete textos que el commit 34173ec aplicó en
                `DetalleDelVencimiento.jsx`: el escenario que lo enseña —el corte
                en curso degradado— no se montaba en ninguna de las escenas
                barridas. Mismo criterio, aplicado aquí.

                Cayeron dos cosas, dichas para que nadie las reponga creyendo que
                faltaban:
                  · «No se muestra un cero: cero significaría…»: explicaba la
                    decisión interna de enviar nulo en vez de cero. El HECHO que a
                    la persona le importa —que la cifra ausente no quiere decir
                    que no gastó— se conserva, con sus palabras.
                  · «El estado del periodo exigible se calculó igual: los dos
                    bloques se degradan por separado»: «bloques» y «degradarse» son
                    vocabulario de quien construye, y además la frase afirmaba algo
                    que no siempre es cierto —RN-288 degrada en los DOS sentidos, y
                    en el panel se ven juntos el renglón sin estado y este aviso—,
                    así que prometía un dato bueno arriba que podía no estarlo. No
                    se sustituye por otra promesa: este aviso habla solo de lo suyo.
                Ninguna cifra, ninguna acción y ninguna condición de degradación
                cambian: solo las palabras. */}
            <span className="ayuda" data-prueba="corte-en-curso-advertencia-ayuda">
              Que no haya cifra no significa que no hayas gastado nada: esta vez no se pudo calcular
              lo que llevas en este ciclo. Inténtalo de nuevo más tarde.
            </span>
          </div>
        ) : (
          <>
            <span className="curso-et">Llevas acumulado en este ciclo</span>
            <span className="curso-val" data-prueba="corte-en-curso-acumulado">
              {formatearEnDivisa(corte.importeAcumulado, codigoISO)}
            </span>

            {/* Texto YA ARMADO por el servidor: la pantalla no compone la frase
                ni compara fechas (RNF-059, RN-289). */}
            <div className="curso-frase" data-prueba="corte-en-curso-texto">{corte.textoCorteEnCurso}</div>

            {corte.sinConsumos && (
              <div className="ayuda" data-prueba="corte-en-curso-sin-consumos">
                Aún no tienes consumos en este corte. Se muestra igual, porque saber cuándo te corta
                ya vale por sí solo.
              </div>
            )}

            {/* DESGLOSE (D-196, RF-126): gastos más disposiciones, MENOS
                devoluciones. El signo menos lo pone esta etiqueta, no el dato
                (RN-301). Solo se dibuja en el Detalle. */}
            {conDesglose && (
              <table className="tabla" data-prueba="desglose-del-ciclo">
                <tbody>
                  <tr>
                    <td>+ Gastos con cargo a la tarjeta</td>
                    <td><b>{formatearEnDivisa(corte.importeGastosDelCiclo, codigoISO)}</b></td>
                  </tr>
                  <tr>
                    <td>+ Disposiciones de efectivo</td>
                    <td><b>{formatearEnDivisa(corte.importeDisposicionesDelCiclo, codigoISO)}</b></td>
                  </tr>
                  <tr>
                    <td>− Devoluciones</td>
                    <td><b>{formatearEnDivisa(corte.importeDevolucionesDelCiclo, codigoISO)}</b></td>
                  </tr>
                  <tr>
                    <td><b>= Acumulado del ciclo</b></td>
                    <td><b>{formatearEnDivisa(corte.importeAcumulado, codigoISO)}</b></td>
                  </tr>
                </tbody>
              </table>
            )}

            <div className="curso-nota">
              Abre el <b>{formatearFecha(corte.fechaInicioDelCiclo)}</b> y corta el{' '}
              <b>{formatearFecha(corte.fechaProximoCorte)}</b> ({corte.diasParaElProximoCorte}
              {corte.diasParaElProximoCorte === 1 ? ' día' : ' días'}). Se pagará hasta el{' '}
              <b>{formatearFecha(corte.fechaLimitePagoDelProximoCorte)}</b>, cuando este ciclo se
              convierta en lo exigible de arriba. Hoy no se paga y no entra en el total por pagar.
            </div>

            {/* Mensualidades que caerán en este corte: SOLO SI LAS HAY (RN-285), y
                son desglose informativo, JAMÁS suma (RN-268, RN-282). */}
            {corte.mensualidadesQueCaeranEnElCorte?.length > 0 && (
              <div className="curso-msi" data-prueba="mensualidades-futuras">
                <div className="curso-msi-t">Mensualidades que caerán en este corte · futuras</div>
                <ul>
                  {corte.mensualidadesQueCaeranEnElCorte.map((m) => (
                    <li key={m.idMensualidad}>
                      {m.descripcionDelGasto} · <b>{m.contadorDeMensualidad}</b> ·{' '}
                      {formatearEnDivisa(m.importeImputadoAlMes, codigoISO)}
                    </li>
                  ))}
                </ul>
                <span className="ayuda">
                  Suman {formatearEnDivisa(corte.totalMensualidadesQueCaeran, codigoISO)} y no se
                  suman a lo acumulado: la compra a meses ya se contó completa el día que se hizo.
                </span>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
}
